import React from 'react';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Instance } from 'mobx-state-tree';

import analyticsService from '~/src/services/analytics';
import { Matter } from '~/src/stores/matterStore';
import { useSyncSingleMatterModal } from './useSyncSingleMatterModal';
import { useIsClioAuthenticated } from '~/src/hooks/useClio';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';
import { getHumanReadableDateTimeFromIsoString } from '~/src/utils/date';
import theme from '~/src/theme';

type SingleMatterSyncInlineProps = {
  matter: Instance<typeof Matter>;
  onSynced?: () => void;
  analyticsEventName: string;
};

const TOOLTIP_TEXT =
  'A manual refresh is required to pull your latest Clio Manage matter data and custom fields into Clio Draft. Once documents are auto-filled, you can make edits to this data in Clio Draft.';

const SingleMatterSyncInline = ({
  matter,
  onSynced,
  analyticsEventName,
}: SingleMatterSyncInlineProps) => {
  const isAuthenticatedWithClio = useIsClioAuthenticated();
  const isUnifiedPlatform = useFeatureAccesses().isUnifiedPlatform;
  const [{ sync }, modal] = useSyncSingleMatterModal(
    analyticsEventName,
    onSynced,
    {
      onMutate: ({ clio_matter_id }) => {
        analyticsService.track(
          `Single Matter Sync from ${analyticsEventName} - Initiated`,
          {
            clioMatterId: clio_matter_id,
          },
        );
      },
    },
  );

  const clioMatterId = matter.externalMatterId;
  if (!clioMatterId || !isAuthenticatedWithClio || !isUnifiedPlatform) {
    return null;
  }

  const lastSynced = matter.lastSynced;
  if (!lastSynced) {
    return null;
  }

  const handleRefresh = () => {
    sync(clioMatterId);
  };

  return (
    <>
      <div className="flex items-center justify-between mt-4 gap-4">
        <span style={{ fontSize: theme.fonts.size.small }}>
          Documents will be auto-filled using Clio Manage data that was
          refreshed on {getHumanReadableDateTimeFromIsoString(lastSynced)}{' '}
          <Tooltip title={TOOLTIP_TEXT} placement="bottom">
            <InfoOutlinedIcon style={{ fontSize: 16, cursor: 'help' }} />
          </Tooltip>
        </span>
        <button
          type="button"
          onClick={handleRefresh}
          className="px-4 py-2 text-sm font-semibold rounded whitespace-nowrap hover:bg-gray-200"
          style={{ backgroundColor: theme.colors.ice, border: 'none' }}
          data-analyticsname={`${analyticsEventName} - Refresh again`}
        >
          Refresh again
        </button>
      </div>
      {modal}
    </>
  );
};

export default SingleMatterSyncInline;
